import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Play, Search, Facebook, Instagram, Linkedin } from "lucide-react";
import VideoModal from "./VideoModal";
import SearchCommand from "./SearchCommand";
import envirLogo from "@/assets/envir-logo.svg";
import envirLogoWhite from "@/assets/envir-logo-white.svg";
import { useLanguage, translations } from "@/i18n/LanguageContext";
import type { Language } from "@/i18n/translations";
import { wasPrerendered } from "@/lib/prerender";

const languages: { code: Language; label: string }[] = [
  { code: "no", label: "NO" },
  { code: "en", label: "EN" },
  { code: "de", label: "DE" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [burgerOpen, setBurgerOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  // Pages with full-bleed dark hero that extends behind the navbar need white logo/text
  const darkHeroPages: string[] = ["/kompetansesenter", "/salgsbetingelser", "/isps-kai", "/team", "/kontakt"];
  const useLightTheme = darkHeroPages.includes(location.pathname) && !scrolled;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when burger is open
  useEffect(() => {
    if (burgerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [burgerOpen]);

  const navLinks = [
    { label: t(translations.nav.services), hash: "tjenester", page: "/#tjenester" },
    { label: t(translations.nav.about), hash: "om-envir", page: "/om-oss" },
    { label: t(translations.nav.news), hash: "nyheter", page: "/nyheter" },
    { label: t(translations.nav.sustainability), hash: "baerekraft", page: "/#baerekraft" },
  ];

  const handleNavClick = useCallback((e: React.MouseEvent, link: { hash: string; page: string }) => {
    if (isHome) {
      e.preventDefault();
      const el = document.getElementById(link.hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
    // else: normal <a> navigation to link.page
  }, [isHome]);

  const bm = translations.burgerMenu;
  const burgerLinks = [
    { label: t(bm.deliveryOfMaterials), href: "/mottak" },
    { label: t(bm.saleOfRecycled), href: "/gjenvunne-masser" },
    { label: t(bm.soilWashingPlant), href: "/jordvaskeanlegg" },
    { label: t(bm.steamingOfSoil), href: "/jorddampeanlegg" },
    { label: t(bm.knowledgeCentre), href: "/kompetansesenter" },
    { label: t(bm.laboratory), href: "/laboratorium" },
    { label: t(bm.ispsQuay), href: "/isps-kai" },
    { label: t(bm.aboutUs), href: "/om-oss" },
    { label: t(bm.ourSustainability), href: "/baerekraft" },
    { label: t(bm.meetThePeople), href: "/team" },
    { label: t(bm.salesTerms), href: "/salgsbetingelser" },
    { label: t(bm.newsAndInsight), href: "/nyheter" },
    { label: t(translations.board.title), href: "/vaart-styre" },
  ];

  return (
    <>
      <motion.nav
        initial={wasPrerendered() ? false : { y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-background/95 backdrop-blur-md shadow-lg py-3 mx-4 md:mx-6 rounded-b-2xl"
            : "bg-transparent py-6"
        }`}
      >
        <div className="section-padding flex items-center justify-between">
          <a href="/" className="relative z-10">
            <img src={useLightTheme ? envirLogoWhite : envirLogo} alt="Envir" className="h-10 md:h-14 w-auto"  loading="lazy" decoding="async" />
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-4 lg:gap-6 xl:gap-8">



            {/* Language switcher */}
            <div className={`flex items-center gap-1 border rounded-full px-1 py-0.5 ${useLightTheme ? "border-primary-foreground/30" : "border-border"}`}>
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={`text-xs font-body font-500 px-2.5 py-1 rounded-full transition-all duration-300 ${
                    language === lang.code
                      ? useLightTheme ? "bg-primary-foreground text-envir-charcoal" : "bg-foreground text-background"
                      : useLightTheme ? "text-primary-foreground/90 hover:text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>

            <a
              href="/kontakt"
              className={`text-xs lg:text-sm font-body font-500 px-4 lg:px-5 py-2 lg:py-2.5 rounded-full whitespace-nowrap transition-all duration-300 ${
                useLightTheme ? "bg-primary-foreground text-envir-charcoal hover:bg-primary-foreground/90" : "bg-foreground text-background hover:bg-foreground/90"
              }`}
            >
              {t(translations.nav.contact)}
            </a>

            {/* Search toggle */}
            <button
              onClick={() => setSearchOpen(true)}
              className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors ${
                useLightTheme ? "border-primary-foreground/30 hover:bg-primary-foreground/10" : "border-border hover:bg-secondary"
              }`}
              aria-label="Search"
            >
              <Search className={`w-4 h-4 ${useLightTheme ? "text-primary-foreground" : "text-foreground"}`} />
            </button>

            {/* Burger toggle (desktop) */}
            <button
              onClick={() => setBurgerOpen(true)}
              className={`h-10 rounded-full border flex items-center gap-2 px-4 transition-colors ${
                useLightTheme ? "border-primary-foreground/30 hover:bg-primary-foreground/10" : "border-border hover:bg-secondary"
              }`}
              aria-label="Open menu"
            >
              <span className={`text-xs font-body font-500 tracking-wide ${useLightTheme ? "text-primary-foreground" : "text-foreground"}`}>
                {t(translations.nav.menu)}
              </span>
              <Menu className={`w-5 h-5 ${useLightTheme ? "text-primary-foreground" : "text-foreground"}`} />
            </button>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setBurgerOpen(!burgerOpen)}
            className="md:hidden relative z-[60] flex flex-col gap-1.5"
            aria-label="Toggle menu"
          >
            <span className={`block w-6 h-0.5 transition-all duration-300 ${burgerOpen ? "bg-primary-foreground rotate-45 translate-y-2" : useLightTheme ? "bg-primary-foreground" : "bg-foreground"}`} />
            <span className={`block w-6 h-0.5 transition-all duration-300 ${burgerOpen ? "bg-primary-foreground opacity-0" : useLightTheme ? "bg-primary-foreground" : "bg-foreground"}`} />
            <span className={`block w-6 h-0.5 transition-all duration-300 ${burgerOpen ? "bg-primary-foreground -rotate-45 -translate-y-2" : useLightTheme ? "bg-primary-foreground" : "bg-foreground"}`} />
          </button>
        </div>
      </motion.nav>

      {/* Full-screen burger menu overlay */}
      <AnimatePresence>
        {burgerOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[55] bg-envir-charcoal"
          >
            {/* Close button */}
            <div className="absolute top-0 left-0 right-0 section-padding py-6 flex items-center justify-between">
              <a href="/" onClick={() => setBurgerOpen(false)}>
                <img src={envirLogoWhite} alt="Envir" className="h-8 md:h-10 w-auto"  loading="lazy" decoding="async" />
              </a>
              <button
                onClick={() => setBurgerOpen(false)}
                className="w-10 h-10 rounded-full border border-primary-foreground/20 flex items-center justify-center hover:bg-primary-foreground/10 transition-colors"
                aria-label="Close menu"
              >
                <X className="w-5 h-5 text-primary-foreground" />
              </button>
            </div>

            {/* Menu content */}
            <div className="h-full overflow-y-auto pt-24 pb-12 section-padding">
              <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-x-16 gap-y-0">
                {/* Links */}
                <div className="space-y-1">
                  {burgerLinks.map((link, i) => (
                    <motion.a
                      key={link.label}
                      href={link.href}
                      onClick={() => setBurgerOpen(false)}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.1 + i * 0.04 }}
                      className="group flex items-center justify-between py-3 border-b border-primary-foreground/10 hover:border-primary/40 transition-colors"
                    >
                      <span className="text-lg md:text-xl font-display font-500 text-primary-foreground/80 group-hover:text-primary-foreground transition-colors">
                        {link.label}
                      </span>
                      <ArrowUpRight className="w-4 h-4 text-primary-foreground/30 group-hover:text-primary transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </motion.a>
                  ))}
                </div>

                {/* Right side: contact info + language */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="mt-12 md:mt-0 flex flex-col justify-between"
                >
                  <div className="space-y-8">
                    <div>
                      <h4 className="text-xs font-body font-500 uppercase tracking-widest text-primary mb-4">
                        {t(translations.contact.label)}
                      </h4>
                      <div className="space-y-3 text-sm font-body text-primary-foreground/50">
                        <a href="tel:+4755200400" className="block hover:text-primary transition-colors">+47 55 200 400</a>
                        <a href="mailto:post@envir.com" className="block hover:text-primary transition-colors">post@envir.com</a>
                        <p>Sjøkrigsskoleveien 15, 5165 Laksevåg</p>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-xs font-body font-500 uppercase tracking-widest text-primary mb-4">
                        {t(translations.contact.hours)}
                      </h4>
                      <p className="text-sm font-body text-primary-foreground/50">
                        {t(translations.contact.hoursValue)}
                      </p>
                    </div>

                    <div>
                      <h4 className="text-xs font-body font-500 uppercase tracking-widest text-primary mb-4">
                        {t(translations.social.followUs)}
                      </h4>
                      <div className="flex items-center gap-4">
                        <a href="https://www.facebook.com/envir.as/" target="_blank" rel="noopener noreferrer" className="text-primary-foreground/50 hover:text-primary transition-colors"><Facebook className="w-5 h-5" /></a>
                        <a href="https://www.instagram.com/envir_as/" target="_blank" rel="noopener noreferrer" className="text-primary-foreground/50 hover:text-primary transition-colors"><Instagram className="w-5 h-5" /></a>
                        <a href="https://www.linkedin.com/company/envir/" target="_blank" rel="noopener noreferrer" className="text-primary-foreground/50 hover:text-primary transition-colors"><Linkedin className="w-5 h-5" /></a>
                      </div>
                    </div>
                  </div>

                  {/* Language + CTA */}
                  <div className="mt-12 space-y-6">
                    <div className="flex items-center gap-2">
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => setLanguage(lang.code)}
                          className={`text-sm font-body font-500 px-4 py-2 rounded-full transition-all duration-300 ${
                            language === lang.code
                              ? "bg-primary text-primary-foreground"
                              : "text-primary-foreground/50 hover:text-primary-foreground border border-primary-foreground/20"
                          }`}
                        >
                          {lang.label}
                        </button>
                      ))}
                    </div>

                    <a
                      href="/kontakt"
                      onClick={() => setBurgerOpen(false)}
                      className="inline-flex items-center px-8 py-4 bg-primary text-primary-foreground font-display font-500 text-sm tracking-wide rounded-full hover:bg-primary/90 transition-all duration-300"
                    >
                      {t(translations.nav.contact)}
                    </a>
                    <button
                      onClick={() => { setBurgerOpen(false); setVideoOpen(true); }}
                      className="inline-flex items-center gap-3 text-sm font-body font-500 text-primary-foreground/60 hover:text-primary-foreground transition-colors"
                    >
                      <Play className="w-4 h-4" />
                      {t(translations.hero.videoLink)}
                    </button>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} />
      <SearchCommand open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
};

export default Navbar;
